import React from 'react'
import { useToast } from '@vtex/admin-ui'
import { useIntl } from 'react-intl'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'

import { useCheckout } from '../../state'
import {
  ConfirmAddressStepContainer,
  HomeDeliveryAddressSteps,
} from './components'
import {
  ConfirmPostalCodeStep,
  NameStep,
  DocumentStep,
  PhoneStep,
  CompanyNameStep,
  TradeNameStep,
  CorporatePhoneStep,
  CorporateDocumentStep,
  ClientProfileContainer,
  EmailStepContainer,
  StateInscriptionStep,
  IdentificationStep,
} from '../../components'
import { CheckoutRoute, PURCHASE_FLOW_ORIGIN_HASH } from '../../constants'
import { isEditMode } from '../../utils/verifyEditionMode'
import { getOrigin } from '../../utils/getOrigin'
import { isClientProfileComplete } from '../../utils'
import type { OrderForm } from '../../../../core/requests/checkout'
import type { ClientDataState } from '../../typings'
import { getClientDataStateFromOrderForm } from '../../utils/getClientDataStateFromOrderForm'
import { checkNameIsAnonymous } from '../../utils/checkNameIsAnonymous'
import { TopbarMenuArea } from '../../../Instore/components/Header/Topbar'
import { BackButton } from '../../../../core/components'
import { useIdentificationStep } from '../../hooks'
import { useAddressValidation } from './hooks'
import { getNextAddressRoute } from '../../utils/getNextAddressRoute'

export const HomeDeliveryScreen = () => {
  const { path } = useRouteMatch()
  const history = useHistory()
  const checkout = useCheckout()
  const clientDataState = checkout.checkout.clientData
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const isEditing = isEditMode()
  const origin = getOrigin() ?? ''
  const identificationStep = useIdentificationStep()
  const isEditingClientProfile = path === '/checkout/edit-profile' // TODO: remove when develop new client profile screen
  const { validate } = useAddressValidation()
  const [nextAddressStep] = getNextAddressRoute(clientDataState.address)

  const handleNameSubmit = (name: ClientDataState['name']) => {
    checkout.setClientDataName(name)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.HomeDeliveryPhone
    )
  }

  const handleDocumentSubmit = (document: ClientDataState['document']) => {
    checkout.setClientDataDocument(document)

    history.push(
      isEditing
        ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
        : CheckoutRoute.HomeDeliveryName
    )
  }

  const handlePhoneSubmit = (phone: ClientDataState['phone']) => {
    checkout.setClientDataPhone(phone)

    const nextStep = isEditing
      ? `${PURCHASE_FLOW_ORIGIN_HASH[origin]}`
      : CheckoutRoute.HomeDeliveryClientProfile

    history.push(nextStep)
  }

  const handleConfirmClientSubmit = (selectedAddress: Address[] | null) => {
    const isSelectedAddressessValid = selectedAddress?.every((address) =>
      validate(address)
    )

    if (isSelectedAddressessValid) {
      history.push(CheckoutRoute.ConfirmAdress)
    } else {
      const nextRoute =
        nextAddressStep === undefined || nextAddressStep?.name === 'street'
          ? ''
          : nextAddressStep.route

      history.push(`${CheckoutRoute.HomeDeliveryAddressSteps}/${nextRoute}`)
    }
  }

  const handleConfirmAddressSubmit = () => {
    if (isEditingClientProfile) {
      showToast({
        key: 'profile-edit-success',
        variant: 'positive',
        message: formatMessage({
          id: 'checkout.edit-profile.edit-success',
        }),
        duration: 5000,
      })

      return history.push('/checkout/cart')
    }

    history.push('../order-summary')
  }

  const handlePostalCodeSubmit = () => {
    history.push(CheckoutRoute.HomeDeliveryAddressSteps)
  }

  const handleCompanyNameSubmit = (
    companyName: ClientDataState['companyInformation']['companyName']
  ) => {
    checkout.setClientDataCompanyName(companyName)

    history.push(
      isEditing
        ? CheckoutRoute.HomeDeliveryClientProfile
        : CheckoutRoute.HomeDeliveryTradeName
    )
  }

  const handleTradeNameSubmit = (
    tradeName: ClientDataState['companyInformation']['tradeName']
  ) => {
    checkout.setClientDataTradeName(tradeName)

    history.push(
      isEditing
        ? CheckoutRoute.HomeDeliveryClientProfile
        : CheckoutRoute.HomeDeliveryCorporatePhone
    )
  }

  const handleCorporatePhoneSubmit = (
    corporatePhone: ClientDataState['companyInformation']['corporatePhone']
  ) => {
    checkout.setClientDataCorporatePhone(corporatePhone)

    history.push(
      isEditing
        ? CheckoutRoute.HomeDeliveryClientProfile
        : CheckoutRoute.HomeDeliveryCorporateDocument
    )
  }

  const handleCorporateDocumentSubmit = (
    corporateDocument: ClientDataState['companyInformation']['corporateDocument']
  ) => {
    checkout.setClientDataCorporateDocument(corporateDocument)

    history.push(
      isEditing
        ? CheckoutRoute.HomeDeliveryClientProfile
        : CheckoutRoute.HomeDeliveryStateInscription
    )
  }

  const handleEmailSubmit = async (orderFormHandled: OrderForm | null) => {
    if (!orderFormHandled) {
      return
    }

    const submittedEmail = orderFormHandled.clientProfileData?.email ?? ''

    if (!isClientProfileComplete(orderFormHandled.clientProfileData)) {
      if (isEditing && clientDataState.email !== submittedEmail) {
        checkout.clearClientDataFromClientDataState()
      }

      checkout.setClientDataEmail(submittedEmail)

      const nextStep = orderFormHandled.shippingData?.address
        ? orderFormHandled.clientProfileData?.document
          ? CheckoutRoute.HomeDeliveryName
          : CheckoutRoute.HomeDeliveryDocument
        : CheckoutRoute.HomeDeliveryConfirmPostalCode

      history.push(nextStep)

      return
    }

    checkout.setClientDataState(
      getClientDataStateFromOrderForm(orderFormHandled)
    )

    history.push(CheckoutRoute.HomeDeliveryClientProfile)
  }

  const handleStateInscriptionSubmit = (
    stateInscription: ClientDataState['companyInformation']['stateInscription']
  ) => {
    checkout.setClientDataStateIncription(stateInscription)
    checkout.setClientDataIsCorporate(true)

    history.push(CheckoutRoute.HomeDeliveryClientProfile)
  }

  const handleIdentifySubmit = (
    submittedOrderForm: OrderForm | null,
    identification: string | null,
    identificationType: string
  ) => {
    const isSelectedAddressessValid = submittedOrderForm?.shippingData.selectedAddresses.every(
      (address) => validate(address)
    )

    const nextHomeDeliveryRoute =
      nextAddressStep === undefined || nextAddressStep?.name === 'street'
        ? CheckoutRoute.HomeDeliveryAddressSteps
        : `${CheckoutRoute.HomeDeliveryAddressSteps}/${nextAddressStep.route}`

    identificationStep.handleStepSubmit(
      submittedOrderForm,
      identification,
      identificationType,
      {
        document: CheckoutRoute.HomeDeliveryDocument,
        email: CheckoutRoute.HomeDeliveryEmail,
        afterIdentification: isSelectedAddressessValid
          ? CheckoutRoute.ConfirmAdress
          : nextHomeDeliveryRoute,
      }
    )
  }

  return (
    <>
      <TopbarMenuArea>
        <BackButton />
      </TopbarMenuArea>
      <Switch>
        <Route path={CheckoutRoute.HomeDeliveryIdentify}>
          <IdentificationStep
            onStepSubmit={handleIdentifySubmit}
            initialIdentification={clientDataState.identification}
          />
        </Route>

        <Route path={CheckoutRoute.HomeDeliveryEmail}>
          <EmailStepContainer
            currentState={clientDataState}
            onStepSubmit={handleEmailSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryName}>
          <NameStep
            isEditing={isEditing}
            data-testid="name-step"
            onStepSubmit={handleNameSubmit}
            initialName={
              checkNameIsAnonymous(clientDataState.name)
                ? ''
                : clientDataState.name
            }
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryDocument}>
          <DocumentStep
            isEditing={isEditing}
            onStepSubmit={handleDocumentSubmit}
            initialDocument={clientDataState.document}
            clientName={clientDataState.name}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryPhone}>
          <PhoneStep
            isEditing={isEditing}
            onStepSubmit={handlePhoneSubmit}
            clientName={clientDataState.name}
            initialPhone={clientDataState.phone}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryClientProfile}>
          <ClientProfileContainer nextStep={handleConfirmClientSubmit} />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryCompanyName}>
          <CompanyNameStep
            isEditing={isEditing}
            data-testid="company-name-step"
            onStepSubmit={handleCompanyNameSubmit}
            initialName={clientDataState.companyInformation.companyName}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryTradeName}>
          <TradeNameStep
            isEditing={isEditing}
            data-testid="trade-name-step"
            onStepSubmit={handleTradeNameSubmit}
            initialName={clientDataState.companyInformation.tradeName}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryCorporatePhone}>
          <CorporatePhoneStep
            isEditing={isEditing}
            onStepSubmit={handleCorporatePhoneSubmit}
            initialPhone={clientDataState.companyInformation.corporatePhone}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryCorporateDocument}>
          <CorporateDocumentStep
            isEditing={isEditing}
            onStepSubmit={handleCorporateDocumentSubmit}
            initialDocument={
              clientDataState.companyInformation.corporateDocument
            }
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryStateInscription}>
          <StateInscriptionStep
            isEdit={isEditing}
            onStepSubmit={handleStateInscriptionSubmit}
            initialStateInscription={
              clientDataState.companyInformation.stateInscription
            }
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryConfirmAdress}>
          <ConfirmAddressStepContainer
            data-testid="confirm-address"
            onStepSubmit={handleConfirmAddressSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryConfirmPostalCode}>
          <ConfirmPostalCodeStep
            data-testid="confirm-postal-code"
            onStepSubmit={handlePostalCodeSubmit}
          />
        </Route>
        <Route path={CheckoutRoute.HomeDeliveryAddressSteps}>
          <HomeDeliveryAddressSteps />
        </Route>
      </Switch>
    </>
  )
}
